import axios from 'axios';
import { Snackbar, Alert } from '@mui/material';
import { createRoot } from 'react-dom/client';
import { getToken, removeToken } from '../utils/auth';
import { auth_refresh } from './modules/auth';
import { isInApp } from '../utils/environment';
// 创建用于显示消息的函数
const showMessage = (message, severity = 'error') => {
  const container = document.createElement('div');
  document.body.appendChild(container);
  
  const root = createRoot(container);
  root.render(
    <Snackbar
      open={true}
      autoHideDuration={3000} // 展示三秒后隐藏弹框
      onClose={() => {
        root.unmount();
        document.body.removeChild(container);
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity={severity} variant="filled">
        {message}
      </Alert>
    </Snackbar>
  );
};

// 创建axios实例
const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://192.168.31.245',
  timeout: 30000, // 增加到30秒
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Access-Control-Expose-Headers': 'Authorization'
  },
  withCredentials: false,
  referrerPolicy: 'strict-origin-when-cross-origin'
});

// 请求拦截器
http.interceptors.request.use(
  (config) => {
    const token = getToken() || localStorage.getItem('firmServiceToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 暂时注释掉设备信息，解决CORS问题
    // config.headers['Device-Info'] = JSON.stringify(getDeviceInfo());
    
    return config;
  },
  (error) => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
http.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (isInApp()) {
      return;
    }
    
    if (!error.response) {
      showMessage('网络连接失败，请检查网络状态');
      return Promise.reject(error);
    }

    const { status } = error.response;
    

    // 特别处理401错误
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const token = localStorage.getItem('firmServiceToken');
        if (!token) {
          throw new Error('No token found');
        }

        const result = await auth_refresh({ refreshToken: token }, {});
        
        if (result?.code === 200) {
          const newToken = result.data.token;
          localStorage.setItem('firmServiceToken', newToken);
          
          // 更新当前请求的token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return http(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // console.error('Token刷新失败:', refreshError);
        // 不要立即清除token和跳转，给予重试机会
        return Promise.reject(refreshError);
      }
    }

    // 网络错误处理
    // console.error('API请求错误:', error);
    
    if (!error.response) {
      // 网络错误，服务器未响应
      showMessage('Network connection failed, please check your network or server status');
    } else {
      // HTTP错误状态码处理
      switch (status) {
        case 403:
          showMessage('No permission to access this resource');
          break;
        case 404:
          showMessage('The requested resource does not exist');
          break;
        case 500:
          showMessage('Server error, please try again later');
          break;
        case 504:
          showMessage('Server response timeout, please try again later');
          break;
        default:
          showMessage(`Request failed (${status})`);
      }
    }
    
    return Promise.reject(error);
  }
);

// 获取设备信息
export const getDeviceInfo = () => {
  // 获取浏览器名称
  const getBrowserName = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
    if (userAgent.indexOf('Safari') > -1) return 'Safari';
    if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
    if (userAgent.indexOf('Edge') > -1) return 'Edge';
    return 'Unknown';
  };

  // 生成简单的浏览器指纹（基于多个因素）
  const generateFingerprint = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('fingerprint', 2, 2);

    const components = [
      navigator.userAgent,
      navigator.language,
      window.screen.colorDepth,
      window.screen.width + 'x' + window.screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');

    // 简单哈希
    let hash = 0;
    for (let i = 0; i < components.length; i++) {
      const char = components.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
  };

  return {
    deviceType: '2',  // 2 代表 WEB
    bizType: '2',     // 2 代表 TAX 业务
    fingerprint: generateFingerprint(),
    deviceName: getBrowserName(),
    osVersion: navigator.platform,
    appVersion: '1.0.0'
  };
};

// 创建安全的API调用函数
export const safeRequest = async (apiCall, fallbackData = null, showErrorMessage = true) => {
  try {
    return await apiCall();
  } catch (error) {
    // console.error('API调用失败:', error);
    
    // 如果需要显示错误消息，且尚未由拦截器处理
    if (showErrorMessage && !error.handled) {
      showMessage(error.message || 'Request failed, please try again later');
      // 标记错误已处理，避免重复显示
      error.handled = true;
    }
    
    // 返回备用数据，避免页面崩溃
    return fallbackData;
  }
};

export default http; 